import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode'; // Importación correcta
import { AuthContextType } from './AuthContextType';
import { AuthProviderProps } from './AuthProviderProps';
import { TokenType } from './TokenType';

// Crear el contexto de autenticación
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar el contexto de autenticación
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthenticationProvider');
    }
    return context;
};

// Proveedor de autenticación
export const AuthenticationProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [id, setId] = useState<string | null>(null);
    const [name, setName] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true); // Estado de carga para controlar el estado inicial

    // Verifica el token al cargar el componente
    useEffect(() => {
        const token = Cookies.get('authSOIToken');
        if (token) {
            try {
                const decodedToken = jwtDecode<TokenType>(token);
                const currentTime = Math.floor(Date.now() / 1000);

                // Verificar que el token no haya expirado
                if (decodedToken.exp > currentTime) {
                    setIsAuthenticated(true);
                    setId(decodedToken.id);
                    setName(decodedToken.name);
                } else {
                    logout();
                }
            } catch (error) {
                logout();
            }
        }
        setLoading(false); // Finaliza la carga una vez que se haya realizado la verificación
    }, []);

    // Método para iniciar sesión y guardar el token en una cookie
    const login = (token: string) => {
        // Cookies.set('authSOIToken', token, {
        //     expires: new Date(jwtDecode<TokenType>(token).exp * 1000),
        //     secure: true,
        //     sameSite: 'Strict',
        // });
        // Determinar si estamos en desarrollo o producción
        const isProduction = process.env.NODE_ENV === 'production';

        // Configuración dinámica de las cookies según el entorno
        Cookies.set('authSOIToken', token, {
            expires: new Date(jwtDecode<TokenType>(token).exp * 1000),
            secure: isProduction,     // Solo usar secure en producción
            sameSite: isProduction ? 'Lax' : 'Strict'  // Ajustar sameSite según el entorno
        });
        
        const decodedToken = jwtDecode<TokenType>(token);
        setIsAuthenticated(true);
        setId(decodedToken.sub);
        setName(decodedToken.name);
        setLoading(false); // La carga también termina cuando el usuario inicia sesión
    };

    // Método para cerrar sesión y eliminar el token
    const logout = () => {
        Cookies.remove('authSOIToken');
        setIsAuthenticated(false);
        setId(null);
        setName(null);
        setLoading(false);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, id, name, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};